// ─── Cluster C — File-Tree Extraction (ZIP, folders, drops, validation) ──────
// Per docs/design/app-js-split-plan-260522.md §3 Module 3.
//
// Exports: walkZip, extractZip, extractFolder, expandZips,
//          filterByExtension, filterAndDisambiguate, ensureJSZip,
//          validateFile, SUPPORTED_EXTS, IMAGE_EXTENSIONS, LEGACY_EXTS,
//          ALL_ACCEPTED_EXTS, REMEDIATE_EXTS, MAX_ZIP_DEPTH.
//
// Globals (read-only at call time):
//   window.adatool?.log    — logging
//   window.JSZip           — set lazily by ensureJSZip
//   window._adaMaxFileSizeMb          — bridged from auth-and-billing.mjs (Phase 9 replaces)

import { showSectionBanner, showBothSectionBanners } from './banners.mjs';

export const SUPPORTED_EXTS = ['.pptx', '.docx', '.pdf', '.xlsx'];
export const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.heic', '.bmp', '.tiff', '.tif'];
export const LEGACY_EXTS = ['.ppt', '.doc'];
export const ALL_ACCEPTED_EXTS = [...SUPPORTED_EXTS, ...LEGACY_EXTS];
export const REMEDIATE_EXTS = [...SUPPORTED_EXTS, ...LEGACY_EXTS];

export const MAX_ZIP_DEPTH = 3;

// ─── Extension filter ──────────────────────────────────────────────────────
// Returns { accepted, rejected } and shows a warning banner for rejected
// files. `exts` is the allow-list, `label` is used in the human-readable
// message. `section` routes the banner to the correct section.
/**
 * @template T
 * @param {Array<T & {name: string}>} files
 * @param {string[]} exts
 * @param {string} label
 * @param {string} [section]
 * @returns {{ accepted: Array<T & {name: string}>, rejected: Array<T & {name: string}> }}
 */
export function filterByExtension(files, exts, label, section) {
    const accepted = [];
    const rejected = [];
    for (const f of files) {
        const name = f.name.toLowerCase();
        if (exts.some(ext => name.endsWith(ext))) {
            accepted.push(f);
        } else {
            rejected.push(f);
        }
    }
    for (const f of accepted) window.adatool?.log?.info('[ada-tool] ACCEPTED (extension):', f.name);
    for (const f of rejected) window.adatool?.log?.info('[ada-tool] REJECTED (extension):', f.name);
    if (rejected.length > 0) {
        const names = rejected.map(f => f.name).join(', ');
        // WHY: rejected.length === 1 guarantees .at(0) is defined; ?. avoids T|undefined from noUncheckedIndexedAccess
        const firstRejectedName = rejected.at(0)?.name ?? '';
        const msg = rejected.length === 1
            ? `"${firstRejectedName}" is not a supported file type. Accepted formats: ${label}`
            : `${rejected.length} unsupported file(s) skipped (${names}). Accepted formats: ${label}`;
        if (section) {
            showSectionBanner(section, 'warning', msg);
        } else {
            showBothSectionBanners('warning', msg);
        }
    }
    return { accepted, rejected };
}

// ─── Tree extraction (shared by ZIP, folder, and nested ZIPs) ─────────────
//
// Both ZIP archives and dropped folders are trees of files. The pipeline:
//   1. Walk the tree → collect {blob, relPath} entries (recurse into nested ZIPs, max depth 3)
//   2. Filter to supported extensions, skip hidden/macOS junk
//   3. Disambiguate duplicate basenames: a/b/c.pptx → a_SLASH_b_SLASH_c.pptx
//   4. Return File[] ready for upload

// Filter and disambiguate a list of entries into lazy extractors.
// Returns {name, extract: () => Promise<File>}[] — extract is called
// just before upload to avoid decompressing all ZIP entries at once.
// For non-ZIP entries (blob already available), extract returns immediately.
/**
 * @param {Array<{relPath: string, extract?: () => Promise<Blob>, blob?: Blob}>} entries
 * @param {string} source
 * @returns {Array<{name: string, extract: () => Promise<File>}>}
 */
export function filterAndDisambiguate(entries, source) {
    const supported = entries.filter(({ relPath }) => {
        const name = relPath.split('/').pop();
        if (!name || name.startsWith('.') || relPath.includes('__MACOSX')) return false;
        return ALL_ACCEPTED_EXTS.some(ext => name.toLowerCase().endsWith(ext));
    });

    if (supported.length === 0) return [];

    /** @type {Record<string, number>} */
    const basenameCounts = {};
    for (const { relPath } of supported) {
        const base = relPath.split('/').pop() ?? '';
        basenameCounts[base] = (basenameCounts[base] || 0) + 1;
    }

    const results = [];
    for (const entry of supported) {
        const { relPath } = entry;
        const basename = relPath.split('/').pop() ?? '';
        let uploadName = basename;
        if ((basenameCounts[basename] ?? 0) > 1) { // WHY: ?? 0 handles noUncheckedIndexedAccess T|undefined
            const ext = basename.substring(basename.lastIndexOf('.'));
            const pathWithoutExt = relPath.substring(0, relPath.lastIndexOf('.'));
            uploadName = pathWithoutExt.replace(/\//g, '_SLASH_') + ext;
        }
        const capturedExtract = entry.extract;
        const capturedBlob = entry.blob;
        const extractFn = capturedExtract
            ? async () => {
                const blob = await capturedExtract();
                return new File([blob], uploadName, { type: blob.type });
              }
            : async () => new File([/** @type {Blob} */ (capturedBlob)], uploadName, { type: /** @type {Blob} */ (capturedBlob).type });
        results.push({ name: uploadName, extract: extractFn });
        window.adatool?.log?.info(`[ada-tool] ${source}:`, relPath, '→', uploadName);
    }

    window.adatool?.log?.info(`[ada-tool] ${source}: ${results.length} supported file(s)`);
    return results;
}

// Lazy-load JSZip only when the user actually uploads a ZIP or drops a
// folder. The library is ~97 KB and is not needed for the common case
// (single-file uploads), so keeping it out of the initial critical path
// speeds up first render. See fix #4 in the prod-login-perf audit.
/** @type {Promise<unknown> | null} */
let _jszipLoadPromise = null;
export async function ensureJSZip() {
    if (window.JSZip) return window.JSZip;
    if (!_jszipLoadPromise) {
        _jszipLoadPromise = new Promise((resolve, reject) => {
            const s = document.createElement('script');
            s.src = '/static/jszip.min.js';
            s.async = true;
            s.onload = () => resolve(window.JSZip);
            s.onerror = () => {
                _jszipLoadPromise = null;  // allow retry on next user action
                reject(new Error('Failed to load jszip'));
            };
            document.head.appendChild(s);
        });
    }
    return _jszipLoadPromise;
}

// Walk a ZIP archive into {relPath, extract} entries, recursing into nested ZIPs.
// Non-ZIP entries use lazy extraction (blob decompressed on demand via extract()).
/**
 * @param {Blob} zipBlob
 * @param {string} prefix
 * @param {number} depth
 * @returns {Promise<Array<{relPath: string, extract: () => Promise<Blob>}>>}
 */
export async function walkZip(zipBlob, prefix, depth) {
    const JSZip = /** @type {{ loadAsync: (blob: Blob) => Promise<{ files: Record<string, { dir: boolean, async: (type: 'blob') => Promise<Blob> }> }> }} */ (await ensureJSZip());
    const zip = await JSZip.loadAsync(zipBlob);
    const entries = [];
    for (const [zipPath, entry] of Object.entries(zip.files)) {
        if (entry.dir) continue;
        const name = zipPath.split('/').pop() ?? '';
        const relPath = prefix ? prefix + '/' + zipPath : zipPath;
        if (name.toLowerCase().endsWith('.zip') && depth < MAX_ZIP_DEPTH) {
            const nestedBlob = await entry.async('blob');
            window.adatool?.log?.info(`[ada-tool] nested ZIP at depth ${depth + 1}: ${relPath}`);
            entries.push(...await walkZip(nestedBlob, relPath.replace(/\.zip$/i, ''), depth + 1));
        } else {
            entries.push({ relPath, extract: () => entry.async('blob') });
        }
    }
    return entries;
}

/**
 * @param {File} zipFile
 * @returns {Promise<Array<{name: string, extract: () => Promise<File>}>>}
 */
export async function extractZip(zipFile) {
    window.adatool?.log?.info('[ada-tool] extracting ZIP:', zipFile.name, zipFile.size, 'bytes');
    const entries = await walkZip(zipFile, '', 0);
    return filterAndDisambiguate(entries, `ZIP ${zipFile.name}`);
}

// Walk a dropped folder via webkitGetAsEntry into {blob, relPath} entries.
// Nested ZIPs found inside folders are also recursed into.
/**
 * @param {DataTransferItemList | DataTransferItem[]} dataTransferItems
 * @returns {Promise<Array<{name: string, extract: () => Promise<File>}>>}
 */
export async function extractFolder(dataTransferItems) {
    /**
     * @param {{ createReader: () => { readEntries: (cb: (entries: unknown[]) => void) => void } }} dirEntry
     * @returns {Promise<unknown[]>}
     */
    async function readEntries(dirEntry) {
        return new Promise((resolve) => {
            const reader = dirEntry.createReader();
            /** @type {unknown[]} */
            const all = [];
            const readBatch = () => {
                reader.readEntries((entries) => {
                    if (entries.length === 0) { resolve(all); return; }
                    all.push(...entries);
                    readBatch();
                });
            };
            readBatch();
        });
    }

    /**
     * @param {{ isFile?: boolean, isDirectory?: boolean, name: string, file?: (cb: (f: File) => void) => void, createReader?: () => { readEntries: (cb: (entries: unknown[]) => void) => void } }} entry
     * @param {string} prefix
     * @returns {Promise<Array<{blob?: File, relPath: string, extract?: () => Promise<Blob>}>>}
     */
    async function walk(entry, prefix) {
        if (entry.isFile) {
            return new Promise((resolve) => {
                entry.file?.(async (f) => {
                    const relPath = prefix ? prefix + '/' + f.name : f.name;
                    if (f.name.toLowerCase().endsWith('.zip')) {
                        window.adatool?.log?.info(`[ada-tool] ZIP inside folder: ${relPath}`);
                        try {
                            const nested = await walkZip(f, relPath.replace(/\.zip$/i, ''), 1);
                            resolve(nested);
                        } catch (e) {
                            window.adatool?.log?.error('nested ZIP failed:', relPath, e);
                            resolve([]);
                        }
                    } else {
                        resolve([{ blob: f, relPath }]);
                    }
                });
            });
        }
        if (entry.isDirectory) {
            const children = await readEntries(/** @type {any} */ (entry));
            const results = [];
            for (const child of children) {
                const childPrefix = prefix ? prefix + '/' + entry.name : entry.name;
                results.push(...await walk(/** @type {any} */ (child), childPrefix));
            }
            return results;
        }
        return [];
    }

    const entries = [];
    for (const item of dataTransferItems) {
        const entry = item.webkitGetAsEntry ? item.webkitGetAsEntry() : null;
        if (entry && entry.isDirectory) {
            entries.push(...await walk(entry, ''));
        }
    }
    return filterAndDisambiguate(entries, 'folder');
}

// Expand ZIP files in a file list, returning flat array of lazy extractors
// ({name, extract: () => Promise<File>}).
/**
 * @param {File[]} fileList
 * @param {string} [section]
 * @returns {Promise<Array<{name: string, extract: () => Promise<File>}>>}
 */
export async function expandZips(fileList, section) {
    const result = [];
    /** @param {string} type @param {string} msg */
    const notify = (type, msg) => section ? showSectionBanner(section, type, msg) : showBothSectionBanners(type, msg);
    for (const f of fileList) {
        if (f.name.toLowerCase().endsWith('.zip')) {
            try {
                const extracted = await extractZip(f);
                if (extracted.length === 0) {
                    notify('warning', `"${f.name}" contains no supported files.`);
                } else {
                    notify('info', `Extracted ${extracted.length} file(s) from "${f.name}".`);
                }
                result.push(...extracted);
            } catch (e) {
                window.adatool?.log?.error('ZIP extraction failed:', f.name, e);
                notify('warning', `"${f.name}" could not be opened as a ZIP file.`);
            }
        } else {
            // Non-ZIP: wrap the File handle as a lazy extractor (File objects are
            // disk handles, not in-memory blobs — essentially zero cost).
            result.push({ name: f.name, extract: async () => f });
        }
    }
    return result;
}

// ─── File validation ───────────────────────────────────────────────────────
// Size + magic-bytes validation; returns an error string or null.
// MAX_FILE_SIZE_MB is bridged from auth-and-billing.mjs (Phase 9 replaces
// the window bridge with a proper import).
/**
 * @param {File} file
 * @returns {Promise<string | null>}
 */
export async function validateFile(file) {
    const name = file.name.toLowerCase();
    window.adatool?.log?.info('[ada-tool] validating:', file.name, 'size:', file.size, 'type:', file.type);

    if (file.size < 100) {
        window.adatool?.log?.info('[ada-tool] REJECTED (too small):', file.name, file.size, 'bytes');
        return `"${file.name}" is too small (less than 100 bytes). This is likely not a valid document.`;
    }

    const maxFileSizeMb = (typeof window !== 'undefined' && typeof window._adaMaxFileSizeMb === 'number')
        ? window._adaMaxFileSizeMb
        : 25;
    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > maxFileSizeMb) {
        window.adatool?.log?.info('[ada-tool] REJECTED (size):', file.name, `${sizeMB.toFixed(0)}MB > ${maxFileSizeMb}MB`);
        return `"${file.name}" is too large (${sizeMB.toFixed(0)} MB). Maximum file size is ${maxFileSizeMb} MB.`;
    }

    const legacy = {'.ppt': '.pptx', '.doc': '.docx'};
    for (const [old, modern] of Object.entries(legacy)) {
        if (name.endsWith(old)) {
            return `"${file.name}" is a legacy ${old} file. Please convert it to ${modern} first (File > Save As > ${modern}).`;
        }
    }

    if (!REMEDIATE_EXTS.some(ext => name.endsWith(ext))) {
        return `"${file.name}": this file type is not supported. Please upload .pptx, .pdf, .docx, .xlsx, a folder, or a .zip`;
    }

    if (file.size === 0) {
        return `"${file.name}" appears to be empty. On mobile, make sure the file is fully downloaded before selecting it.`;
    }

    if (IMAGE_EXTENSIONS.some(ext => name.endsWith(ext))) return null;

    // Check file header for Office/PDF files (Layer 1 of corrupt-input
    // defense; see web/file_format_check.py for the server-side mirror
    // and docs/design/test-strategy-audit-260508.md §10).
    //
    // Each declared extension has a required magic-byte prefix:
    //   .pdf            → '%PDF' (0x25 0x50 0x44 0x46)
    //   .pptx/.docx/.xlsx → 'PK' ZIP local-file-header (0x50 0x4B + variant)
    //
    // OLE compound (0xD0 0xCF 0x11 0xE0) flags legacy .ppt/.doc that
    // were renamed to .pptx/.docx — surface a specific repair message.
    const blob = file.slice(0, 8);
    const buf = typeof blob.arrayBuffer === 'function'
        ? await blob.arrayBuffer()
        : await new Promise((resolve, reject) => {
            const r = new FileReader();
            r.onload = () => resolve(r.result);
            r.onerror = () => reject(r.error);
            r.readAsArrayBuffer(blob);
        });
    const header = new Uint8Array(buf);
    const headerHex = Array.from(header).map(b => b.toString(16).padStart(2, '0')).join(' ');
    const isZip = header[0] === 0x50 && header[1] === 0x4B
        && (header[2] === 0x03 || header[2] === 0x05 || header[2] === 0x07);
    const isPdf = header[0] === 0x25 && header[1] === 0x50 && header[2] === 0x44 && header[3] === 0x46;
    const isOle = header[0] === 0xD0 && header[1] === 0xCF && header[2] === 0x11 && header[3] === 0xE0;
    if (isOle) {
        window.adatool?.log?.info('[ada-tool] REJECTED (header):', file.name, '— OLE/legacy format');
        return `"${file.name}" appears to be a legacy or encrypted Office file. Please re-save it as a modern ${name.endsWith('.docx') ? '.docx' : '.pptx'} file in Office (File > Save As).`;
    }
    if (name.endsWith('.pdf')) {
        if (!isPdf) {
            window.adatool?.log?.info('[ada-tool] REJECTED (header mismatch):', file.name, 'expected %PDF, got', headerHex);
            return `"${file.name}" does not appear to be a valid PDF (file does not start with %PDF). If it was downloaded from a website, the download may have returned an error page instead.`;
        }
    } else if (name.endsWith('.pptx') || name.endsWith('.docx') || name.endsWith('.xlsx')) {
        if (!isZip) {
            const fmt = name.slice(name.lastIndexOf('.') + 1).toUpperCase();
            window.adatool?.log?.info('[ada-tool] REJECTED (header mismatch):', file.name, `expected PK (ZIP), got`, headerHex);
            return `"${file.name}" does not appear to be a valid ${fmt} file. Modern Office files are ZIP archives starting with PK; this file does not.`;
        }
    } else if (!isZip && !isPdf) {
        window.adatool?.log?.info('[ada-tool] REJECTED (header):', file.name, '— bad magic bytes', headerHex);
        return `"${file.name}" could not be read. If this file is stored in OneDrive or iCloud, open it first to download it locally, then try again.`;
    }
    window.adatool?.log?.info('[ada-tool] ACCEPTED (validated):', file.name);
    return null;
}
